import React, { FC, useState } from "react";
import { Avatar, Table, Tag, Tooltip } from "antd";
// Grid/list toggle, mail and task glyphs have no counterpart in the app set.
import {
  UserOutlined,
  MailOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import { BeamPerson } from "./types";
import BeamIcon from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * People message type.
 *
 * Two densities for the same data: cards when you are browsing faces, a compact
 * table when you are scanning IDs. Phone and email are real tel:/mailto: links
 * so a click dials or composes instead of just displaying a string.
 */

interface PeopleCardProps {
  people: BeamPerson[];
  onViewBio?: (person: BeamPerson) => void;
  onSendMessage?: (person: BeamPerson) => void;
  onAssignTask?: (person: BeamPerson) => void;
}

const ROLE_COLORS: Record<string, string> = {
  "Outreach Lead": "blue",
  Nurse: "green",
  Driver: "orange",
  Client: "purple",
};

const formatPhone = (raw: string) => {
  const digits = raw.replace(/[^\d]/g, "").slice(-10);
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const PeopleCard: FC<PeopleCardProps> = ({
  people,
  onViewBio,
  onSendMessage,
  onAssignTask,
}) => {
  const [view, setView] = useState<"grid" | "table">("grid");

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (name: string, row: BeamPerson) => (
        <span className="mt-people__cell-name">
          <Avatar size={24} src={row.avatar} icon={<UserOutlined />} />
          {name}
        </span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 130,
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || "default"}>{role}</Tag>
      ),
    },
    {
      title: "ID",
      dataIndex: "badgeId",
      width: 110,
      render: (id: string) => <span className="mt-mono">{id}</span>,
    },
    {
      title: "Contact",
      dataIndex: "phone",
      width: 160,
      render: (_: string, row: BeamPerson) => (
        <span className="mt-people__cell-contact">
          <a href={`tel:${row.phone}`}>{formatPhone(row.phone)}</a>
          <a href={`mailto:${row.email}`}>{row.email}</a>
        </span>
      ),
    },
  ];

  return (
    <section className="mt-card mt-people">
      <header className="mt-card__head">
        <h4>People</h4>
        <div className="mt-segmented mt-segmented--icon">
          <button
            type="button"
            className={view === "grid" ? "is-active" : ""}
            onClick={() => setView("grid")}
            aria-label="Card view"
          >
            <AppstoreOutlined />
          </button>
          <button
            type="button"
            className={view === "table" ? "is-active" : ""}
            onClick={() => setView("table")}
            aria-label="Table view"
          >
            <UnorderedListOutlined />
          </button>
        </div>
      </header>

      <div
        className={`mt-card__body ${
          view === "table" ? "mt-card__body--flush" : ""
        }`}
      >
        {view === "grid" ? (
          <div className="mt-people__grid">
            {people.map((person) => (
              <article key={person.id} className="mt-person">
                <Avatar
                  size={52}
                  src={person.avatar}
                  icon={<UserOutlined />}
                  className="mt-person__avatar"
                />
                <h5 className="mt-person__name">{person.name}</h5>
                <Tag
                  color={ROLE_COLORS[person.role] || "default"}
                  className="mt-person__role"
                >
                  {person.role}
                </Tag>
                <span className="mt-mono mt-person__id">{person.badgeId}</span>

                <div className="mt-person__contact">
                  <a href={`tel:${person.phone}`}>
                    <BeamIcon name="call" size={12} /> {formatPhone(person.phone)}
                  </a>
                  <a href={`mailto:${person.email}`}>
                    <MailOutlined /> {person.email}
                  </a>
                </div>

                {/* Each action appears only where the host passed a handler.
                    A button that silently does nothing is worse than no
                    button — the phone and mail links above still work. */}
                <div className="mt-person__actions">
                  {onViewBio && (
                    <Tooltip title="View bio">
                      <button type="button" onClick={() => onViewBio(person)}>
                        <BeamIcon name="user" size={14} />
                      </button>
                    </Tooltip>
                  )}
                  {onSendMessage && (
                    <Tooltip title="Send message">
                      <button
                        type="button"
                        onClick={() => onSendMessage(person)}
                      >
                        <BeamIcon name="message" size={14} />
                      </button>
                    </Tooltip>
                  )}
                  {onAssignTask && (
                    <Tooltip title="Assign task">
                      <button type="button" onClick={() => onAssignTask(person)}>
                        <CheckSquareOutlined />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <Table
            className="mt-people__table"
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={people}
            columns={columns as any}
          />
        )}
      </div>
    </section>
  );
};

export default PeopleCard;
